import React, { useState, useEffect, useRef } from 'react';

const ResultsInActionSection = ({
  title = 'Results in Action',
  subtitle = 'Real outcomes from programs led by Yomuma Likessa and his team',
  achievements = [],
  ctaText = 'Get the Full Case Study Pack',
  onCtaClick = () => console.log('Get the Full Case Study Pack clicked')
}) => {
  const [animationKey, setAnimationKey] = useState(0);
  const sectionRef = useRef(null);
  const wasIntersectingRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !wasIntersectingRef.current) {
            // Only trigger animation when entering viewport (not already visible)
            setAnimationKey(prev => prev + 1);
            wasIntersectingRef.current = true;
          } else if (!entry.isIntersecting) {
            // Reset flag when leaving viewport to allow re-animation
            wasIntersectingRef.current = false;
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the section is visible
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before it's fully in view
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []);
  const defaultAchievements = achievements.length
    ? achievements
    : [
        {
          heading: 'EdTech Fellowship Program',
          body: '3 fellows coached → all secured follow-on funding within 6 months.'
        },
        {
          heading: 'Accelerator Partnership',
          body: 'Delivered founder training & diagnostics for NGO-funded program—20+ startups achieved product-market fit and investor readiness.'
        },
        {
          heading: 'Donor-Funded Incubation',
          body: 'Co-designed program framework for MSME growth → replicated by regional partners in Rwanda and Kenya.'
        }
      ];

  const impactNumbers = [
    { emoji: '💼', number: '100+', text: 'Startups Accelerated' },
    { emoji: '💰', number: '$1,100,000+', text: 'in Funding Unlocked' },
    { emoji: '👩‍💼', number: '500+', text: 'Women Entrepreneurs Empowered' },
    { emoji: '🏢', number: '150+', text: 'SMEs Strengthened' },
    { emoji: '🌱', number: '70+', text: 'Green & Inclusive Innovations Supported' },
    { emoji: '🧭', number: "Designed Ethiopia's First", text: 'Government-Backed Incubation & Acceleration Framework', isSpecial: true }
  ];

  return (
    <section ref={sectionRef} className="bg-gray-50 py-16 lg:py-20 w-full">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 lg:mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle ? (
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {impactNumbers.map((item, index) => (
              <div
                key={`${animationKey}-${index}`}
                className={`bg-white rounded-xl shadow-md border border-gray-100 p-6 sm:p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl fade-in-up ${item.isSpecial ? 'sm:col-span-2 lg:col-span-1' : ''}`}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="text-4xl mb-4">{item.emoji}</div>
                <div className={`${item.isSpecial ? 'text-xl sm:text-2xl' : 'text-3xl sm:text-4xl'} font-bold text-gray-900 mb-2`}>
                  {item.number}
                </div>
                <div className="text-gray-700 text-base sm:text-lg">{item.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResultsInActionSection;

